import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { QuickReviewListRequest } from "../../../../data-model/quickReviewModel";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { QuickReviewTableData } from "../utils/quickReviewTableUtils";
import { recursivelyGeneratePayloadForInitialFetch } from "../utils/recursivelyGeneratePayloadForInitialFetch";
import {
  QuickReviewOrdersState,
  QUICK_REVIEW_HITS_PER_PAGE,
} from "./useQuickReviewOrdersTableData";

/**
 * Orchestrates the initial data fetch for the Quick Review board table.
 * Generates payloads for all pages up to the previously fetched batch (from
 * table context), then triggers the bulk fetch. Re-fetches when toggled via
 * setInitialDataFetchingExecuted(false). No filters or search — the board
 * shows every pending row for the org.
 *
 * @param setQuickReviewOrdersState - State setter for the board loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @returns initialDataFetchingExecuted flag and its setter for re-fetch control
 */
export const useInitialDataFetchingForTable = (
  setQuickReviewOrdersState: React.Dispatch<
    React.SetStateAction<QuickReviewOrdersState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<QuickReviewTableData[]>>,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState(false);

  const { getTableDataFromContext } = useTablesContext();

  const tableAttributes = getTableDataFromContext(
    TableNames.QUICK_REVIEW_TABLE,
  );

  useEffect(() => {
    if (
      !initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      const payload: QuickReviewListRequest = {
        page: 0,
        hits_per_page: QUICK_REVIEW_HITS_PER_PAGE,
      };
      const payloads = recursivelyGeneratePayloadForInitialFetch(
        payload,
        tableAttributes?.lastBatchFetched ?? 0,
      );

      makePostCallForInitialDataFetch(
        payloads,
        authStatusOptions as AuthStatusOptionModel[],
        setQuickReviewOrdersState,
        setTableData,
        setInitialDataFetchingExecuted,
      );
    }
  }, [authStatusOptions, initialDataFetchingExecuted]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
